import React, { useEffect, useState, useCallback } from "react";
import Backend from "../../../../../Backend/Backend";
import Select from "react-select";
import { FiSearch } from "react-icons/fi";
import ErrorSoundFile from "../../../../../Sound/error.mp3";
import toast from "react-hot-toast";
import useSound from "use-sound";

  // =============================
  // REACT SELECT WITH SEARCH ICON
  // =============================
  const SelectWithIcon = ({ icon: Icon, width = "170px", ...props }) => (
    <div className="relative" style={{ width }}>
      <Icon
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[18px] z-10"
        style={{ color: "var(--text)" }}
      />

      <Select
        {...props}
        styles={{
          ...props.styles,
          control: (base) => ({
            ...props.styles.control(base),
            paddingLeft: "28px", // space for icon
          }),
        }}
      />
    </div>
  );

export default function Filter({
  clear,
  reload,
  userType,
  loadStanding,
  search,
  records,
}) {
  const [play] = useSound(ErrorSoundFile);

  const [backend, setBackend] = useState(null);
  const [loading, setLoading] = useState(false);

  const [superId, setSuperId] = useState(null);
  const [masterId, setMasterId] = useState(null);
  const [userId, setUserId] = useState(null);

  const [supers, setSupers] = useState([]);
  const [masters, setMasters] = useState([]);
  const [users, setUsers] = useState([]);

  const [superCache, setSuperCache] = useState([]);
  const [masterCache, setMasterCache] = useState([]);
  const [userCache, setUserCache] = useState([]);

  const [searchSymbol, setSearchSymbol] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);



  // =============================
  // INIT BACKEND
  // =============================
  useEffect(() => {
    (async () => {
      const instance = await new Backend().init();
      setBackend(instance);
    })();
  }, []);

  // LOAD INITIAL FILTER
  useEffect(() => {
    if (!backend) return;
    if (userType === 1) loadSuperFilter();
    if (userType === 2) loadMasterFilter();
    if (userType === 3) loadUserFilter();
    if (userType === 5) loadBrokerUserFilter();
  }, [backend, userType]);

  const getBaseData = useCallback(
    () => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      standing: "1",
    }),
    [],
  );

  const loadFilter = async (extraParams, setList, setCache) => {
    if (!backend) return;

    try {
      setLoading(true);
      const res = await backend.user_filter({
        ...getBaseData(),
        ...extraParams,
      });

      if (res.error === "False") {
        setList(res.user);
        setCache(res.user);
      } else toast.error(res.message);
    } catch {
      toast.error("Failed to load filter");
    } finally {
      setLoading(false);
    }
  };

  const loadSuperFilter = () =>
    loadFilter(
      { admin_id: localStorage.getItem("id") },
      setSupers,
      setSuperCache,
    );

  const loadMasterFilter = (super_id) => {
    const id = userType === 2 ? localStorage.getItem("id") : super_id;
    loadFilter({ super_id: id }, setMasters, setMasterCache);
  };

  const loadUserFilter = (master_id) => {
    const id = userType === 3 ? localStorage.getItem("id") : master_id;
    loadFilter({ master_id: id }, setUsers, setUserCache);
  };

  const loadBrokerUserFilter = (broker_id) => {
    const id = userType === 5 ? localStorage.getItem("id") : broker_id;
    loadFilter({ broker_id: id }, setUsers, setUserCache);
  };

  const handleSearch = (q, cache, setList, key) => {
    if (q.length > 1) {
      setList(
        cache.filter((x) => x[key]?.toLowerCase().includes(q.toLowerCase())),
      );
    } else setList(cache);
  };

  const handleClear = () => {
    clear();
    setSuperId(null);
    setMasterId(null);
    setUserId(null);
    setMasters([]);
    setUsers([]);
  };

  // =============================
  // SQ-OFF ALL
  // =============================
  const SqAll = async () => {
    setLoading(true);
    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      user_id: localStorage.getItem("id"),
    };

    try {
      const res = await backend.sq_all(data);
      setLoading(false);

      if (res.error === "False") {
        toast.success(res.message);
        loadStanding();

        if (userType === 1) {
          loadSuperFilter();
          setMasters([]);
          setUsers([]);
        } else if (userType === 2) {
          loadMasterFilter();
          setUsers([]);
        } else if (userType === 3) loadUserFilter();

        reload?.();
      } else {
        if (res.logout === 1) window.LogOut();
        else {
          play();
          toast.error(res.message);
        }
      }
    } catch {
      setLoading(false);
      play();
      toast.error("Failed to Sq-Off All");
    }
  };

  const confirmSqAll = () => {
    setShowConfirm(false);
    SqAll();
  };

  const cancelSqAll = () => setShowConfirm(false);

  // =============================
  // React Select Styles
  // =============================
  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: "30px",
      minHeight: "36px",
      border: "none",
      backgroundColor: "var(--hover)",
      paddingLeft: "6px",
      boxShadow: "none",
      color: "var(--text)",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--background)",
      borderRadius: "8px",
      border: "1px solid var(--hover)",
      padding: "4px",
      color: "var(--text)",
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor: isFocused ? "var(--primary)" : "transparent",
      color: isFocused ? "var(--white)" : "var(--text)",
      cursor: "pointer",
      borderRadius: "6px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--text)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--text)",
      opacity: 0.6,
    }),
  };

  return (
    <div
      className="h-[60px] flex items-center  justify-between px-4"
      style={{
        background: "var(--background)",
        borderColor: "var(--hover)",
        color: "var(--text)",
      }}
    >
      {/* LEFT SIDE */}
      <div className="flex items-center gap-3">
        <div
          className="px-3 py-1 rounded-full text-sm font-medium"
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
          }}
        >
          Records: {records || 0}
        </div>

        {(userType >= 1 || userType <= 3) && (
          <input
            type="text"
            placeholder="Search Symbol"
            value={searchSymbol}
            onChange={(e) => {
              setSearchSymbol(e.target.value);
              search(e.target.value);
            }}
            className="w-[180px] px-4 py-1 rounded-full text-sm focus:ring-2 outline-none"
            style={{
              background: "var(--background)",
              border: "2px solid var(--hover)",
              color: "var(--text)",
            }}
          />
        )}
      </div>

      {/* RIGHT SIDE */}
      <div className="flex items-center gap-2">
        {/* SUPER SELECT */}
        {userType === 1 &&
          (!superId ? (
            <SelectWithIcon
              icon={FiSearch}
              width="200px"
              placeholder="Select Super"
              styles={selectStyles}
              isSearchable
              isDisabled={!backend || loading}
              options={supers.map((i) => ({
                label: i.super_username,
                value: i,
              }))}
              onInputChange={(q) =>
                handleSearch(q, superCache, setSupers, "super_username")
              }
              onChange={(s) => {
                setSuperId(s.value);
                loadStanding(s.value.super_id);
                loadMasterFilter(s.value.super_id);
              }}
            />
          ) : (
            <div
              className="px-3 py-1 rounded-full  font-semibold text-sm"
              style={{
                background: "var(--background)",
                border: "2px solid var(--hover)",
                color: "var(--text)",
              }}
            >
              SUPER: {superId.super_username}
            </div>
          ))}

        {/* MASTER SELECT */}
        {(userType === 1 || userType === 2) &&
          (!masterId ? (
            <SelectWithIcon
              icon={FiSearch}
              width="200px"
              placeholder="Select Master"
              styles={selectStyles}
              isSearchable
              isDisabled={!backend || loading}
              options={masters.map((i) => ({
                label: i.master_username,
                value: i,
              }))}
              onInputChange={(q) =>
                handleSearch(q, masterCache, setMasters, "master_username")
              }
              onChange={(s) => {
                setMasterId(s.value);
                loadStanding(superId?.super_id, s.value.master_id);
                loadUserFilter(s.value.master_id);
              }}
            />
          ) : (
            <div
              className="px-3 py-1 font-semibold rounded-full text-sm"
              style={{
                background: "var(--background)",
                border: "2px solid var(--hover)",
                color: "var(--text)",
              }}
            >
              MASTER: {masterId.master_username}
            </div>
          ))}

        {/* USER SELECT */}
        {(userType === 1 ||
          userType === 2 ||
          userType === 3 ||
          userType === 5) &&
          (!userId ? (
            <SelectWithIcon
              icon={FiSearch}
              width="200px"
              placeholder="Select User"
              styles={selectStyles}
              isSearchable
              isDisabled={!backend || loading}
              options={users.map((i) => ({
                label: i.username,
                value: i,
              }))}
              onInputChange={(q) =>
                handleSearch(q, userCache, setUsers, "username")
              }
              onChange={(s) => {
                setUserId(s.value);
                loadStanding(
                  superId?.super_id,
                  masterId?.master_id,
                  s.value.user_id,
                );
              }}
            />
          ) : (
            <div
              className="px-3 py-1 font-semibold rounded-full text-sm"
              style={{
                background: "var(--background)",
                border: "2px solid var(--hover)",
                color: "var(--text)",
              }}
            >
              USER: {userId.username}
            </div>
          ))}

        {/* SQ-OFF ALL */}
        {userType === 4 && (
          <>
            <input
              type="text"
              placeholder="Search Symbol"
              value={searchSymbol}
              onChange={(e) => {
                setSearchSymbol(e.target.value);
                search(e.target.value);
              }}
              className="w-[180px] px-4 py-1 rounded-full text-sm focus:ring-2 outline-none"
              style={{
                background: "var(--background)",
                border: "2px solid var(--hover)",
                color: "var(--text)",
              }}
            />

            <button
              onClick={() => !loading && setShowConfirm(true)}
              className="px-4 py-1.5 rounded-md bg-[var(--sell)] text-white text-sm font-semibold shadow"
            >
              SQ-OFF ALL
            </button>
          </>
        )}

        {/* CLEAR */}
        {(userType === 1 || userType === 2 || userType === 3 || userType === 5) && (
          <button
            onClick={handleClear}
            disabled={loading}
            className="px-4 py-1.5 rounded-full text-sm font-semibold shadow"
            style={{
              background: "var(--sell)",
              color: "var(--white)",
            }}
          >
            CLEAR
          </button>
        )}

        {/* RELOAD */}
        <button
          onClick={reload}
          disabled={loading}
          className="px-4 py-1.5 rounded-full text-sm font-semibold shadow"
          style={{
            background: "var(--primary)",
            color: "var(--white)",
          }}
        >
          RELOAD
        </button>
      </div>

      {/* CONFIRM MODAL */}
      {showConfirm && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <div
            className="p-6 rounded-lg w-[320px] shadow-xl"
            style={{
              background: "var(--background)",
              border: "1px solid var(--hover)",
              color: "var(--text)",
            }}
          >
            <h3 className="text-xl font-bold mb-2">Confirm Sq-Off All</h3>
            <p className="mb-4">
              Are you sure you want to Sq-Off all standing?
            </p>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={cancelSqAll}
                className="px-4 py-1.5 rounded-md text-sm"
                style={{
                  background: "var(--hover)",
                  color: "var(--text)",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSqAll}
                className="px-4 py-1.5 rounded-md text-sm font-semibold"
                style={{
                  background: "red",
                  color: "#fff",
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
